import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-community/core'
import {
  getFilterOnCellContextMenu,
  getSelectedForContextMenu,
} from '@helios/ag-grid-config'
import {
  CASH_ENTRIES,
  ClearwaterTransaction,
  ExternalTransaction,
  LinkedStatus,
  TranCategory,
  TransactionTypeEnum,
  checkIsMatchAdditionalDisabled,
  checkIsMatchDisabled,
  checkIsMatchUsingExternalDisabled,
  checkSomeAvailableActionsIncludes,
  getMatchErrorMessage,
  getMatchUsingExternalErrorMessage,
  isManyToManyMatch,
} from '@helios/common-api'
import {
  SecurityInfo,
  getTransactionCategoryFromCode,
  getTransactionCategoryFromTypeKey,
  getTransactionCategoryFromTypeKeyForBulk,
  getTransactionTypeKeyFromCode,
} from '@helios/create-transactions-modal'
import { HighlightingService } from '@helios/generic-components'
import { ClearwaterTransactionsTaskService } from '../services/clearwater-transactions-task.service'
import { isLinked } from './link-status-renderer'

/**
 * Get context menu for clearwater transactions grid.
 */
export function getClearwaterTransactionsContextMenuItems(
  params: GetContextMenuItemsParams,
  extTransSelected: ExternalTransaction[],
  taskService: ClearwaterTransactionsTaskService,
  highlightService: HighlightingService,
  cacheLoaded: boolean,
  isSleeveAggregateLoaded: boolean,
  selectAllTxEnabled: boolean,
  selectAllTransactionsData: ClearwaterTransaction[],
  hasExternalFileViewerPermissions: boolean,
  isEnfusionAccount?: boolean,
): ('separator' | MenuItemDef)[] {
  const menuItems: ('separator' | MenuItemDef)[] = []

  const cwTransSelected: ClearwaterTransaction[] = selectAllTxEnabled
    ? selectAllTransactionsData
    : getSelectedForContextMenu<ClearwaterTransaction>(params)
  const cwTran: ClearwaterTransaction = cwTransSelected[0]

  // When no rows are selected, avoid showing menu items
  // to prevent us from getting into weird states.
  if (cwTransSelected.length === 0) {
    return menuItems
  }
  const isAdditionalMatchDisabled = checkIsMatchAdditionalDisabled(
    cwTransSelected,
    extTransSelected,
  )
  const isTransSameCategory = checkTransactionsCategory(cwTransSelected)
  const selectedTransId: string[] = []
  const selectedTransCode: string[] = []
  const securityData: SecurityInfo[] = []
  const securityId: number[] = []
  let isTransEditable: boolean | undefined = true
  const isBulkTransferOutNonEditableTransaction =
    checkBulkTransferOutNonEditableTransaction(cwTransSelected)
  cwTransSelected.forEach(cwSelected => {
    const tranCategory = getTransactionCategoryFromCode(cwSelected.tranTypeCode)
    selectedTransId.push(cwSelected.tranId)
    selectedTransCode.push(cwSelected.tranTypeCode)
    if (cwSelected.secId && tranCategory != TranCategory.TRADE)
      securityId.push(cwSelected.secId)
    if (
      cwSelected.secId &&
      cwSelected.settleDate &&
      tranCategory == TranCategory.TRADE
    )
      securityData.push({
        securityId: cwSelected.secId,
        settleDate: cwSelected.settleDate,
        accountId: cwSelected.accountId,
      })

    isTransEditable = isTransEditable
      ? cwSelected.availableActions?.includes('editTran')
      : false
  })
  const suffix =
    cwTransSelected.length > 1 ? ' (' + cwTransSelected.length + ')' : ''
  return [
    {
      name: 'Clear' + suffix,
      disabled:
        !checkSomeAvailableActionsIncludes(cwTransSelected, 'clear') ||
        cwTransSelected.length > 10000,
      tooltip:
        cwTransSelected.length > 10000
          ? 'You cannot clear over 10000 transactions at a time'
          : '',
      action: () => taskService.clear(cwTransSelected),
    },
    {
      name: 'Clear & Set Post Date' + suffix,
      disabled:
        !checkSomeAvailableActionsIncludes(
          cwTransSelected,
          'clearAndSetPost',
        ) || cwTransSelected.length > 10000,
      tooltip:
        cwTransSelected.length > 10000
          ? 'You cannot clear over 10000 transactions at a time'
          : '',
      action: () => taskService.clearAndSetPostDate(cwTransSelected),
    },
    {
      name: 'Unclear' + suffix,
      disabled:
        !checkSomeAvailableActionsIncludes(cwTransSelected, 'unclear') ||
        cwTransSelected.length > 10000,
      tooltip:
        cwTransSelected.length > 10000
          ? 'You cannot unclear over 10000 transactions at a time'
          : '',
      action: () => taskService.unclear(cwTransSelected),
    },
    {
      name: 'Delete' + suffix,
      disabled:
        !checkSomeAvailableActionsIncludes(cwTransSelected, 'delete') ||
        cwTransSelected.length > 10000,
      tooltip:
        cwTransSelected.length > 10000
          ? 'You cannot delete over 10000 transactions at a time'
          : '',
      action: () => taskService.delete(cwTransSelected),
    },
    {
      name: 'Delete & Ignore' + suffix,
      disabled:
        !checkSomeAvailableActionsIncludes(
          cwTransSelected,
          'deleteAndIgnore',
        ) || cwTransSelected.length > 10000,
      tooltip:
        cwTransSelected.length > 10000
          ? 'You cannot delete over 10000 transactions at a time'
          : '',
      action: () => taskService.deleteAndIgnore(cwTransSelected),
    },
    {
      name: 'Unmatch' + suffix,
      disabled:
        checkIsUnmatchMenuItemDisabled(cwTransSelected) ||
        cwTransSelected.length > 10000,
      tooltip:
        cwTransSelected.length > 10000
          ? 'You cannot unmatch over 10000 transactions at a time'
          : '',
      action: () => taskService.unmatch(cwTransSelected, isEnfusionAccount),
    },
    {
      name: 'Match' + (isAdditionalMatchDisabled ? '' : ' Additional') + suffix,
      disabled:
        (isAdditionalMatchDisabled
          ? checkIsMatchDisabled(cwTransSelected, extTransSelected)
          : false) || cwTransSelected.length > 10000,
      tooltip:
        cwTransSelected.length > 10000
          ? 'You cannot match over 10000 transactions at a time'
          : isAdditionalMatchDisabled
          ? getMatchMenuItemTooltip(cwTransSelected, extTransSelected)
          : 'Match Additional matches to already matched transactions',
      action: () =>
        isManyToManyMatch(cwTransSelected, extTransSelected)
          ? taskService.manyToManyMatch(cwTransSelected, extTransSelected)
          : taskService.match(cwTransSelected, extTransSelected),
    },
    {
      name: 'Match Using External Data',
      disabled:
        !checkTransactionsCategory(cwTransSelected) ||
        checkIsMatchUsingExternalDisabled(cwTransSelected, extTransSelected) ||
        cwTransSelected.length > 10000,
      tooltip:
        cwTransSelected.length > 10000
          ? 'You cannot match over 10000 transactions at a time'
          : getMatchUsingExternalMenuItemTooltip(
              cwTransSelected,
              extTransSelected,
            ),
      action: () =>
        taskService.matchUsingExternal(cwTransSelected, extTransSelected),
    },
    {
      name: 'Find in External File Viewer' + suffix,
      disabled:
        !hasExternalFileViewerPermissions ||
        cwTran.linkedStatus === LinkedStatus.UNMATCHED ||
        cwTransSelected.length > 1,
      tooltip: !hasExternalFileViewerPermissions
        ? 'Feature not available due to lack of permission'
        : cwTransSelected.length > 1
        ? 'Select a single transaction to view in raw data file'
        : 'View in raw data file',
      action: () => taskService.findTransInExternalFile(cwTran),
    },
    'separator',
    {
      name: 'Find matched transaction' + suffix,
      disabled: cwTran.linkedStatus === LinkedStatus.UNMATCHED,
      tooltip: 'Filter External transactions by matched transaction',
      action: () => taskService.findMatchedTransaction(cwTransSelected),
    },
    ...getFilterOnCellContextMenu(params),
    'separator',
    {
      name: 'Edit Note' + suffix,
      disabled: !cwTran.availableActions?.includes('editNote'),
      action: () => taskService.editNotes(cwTransSelected),
    },
    {
      name: 'Delete Note' + suffix,
      disabled:
        !cwTran.availableActions?.includes('editNote') ||
        !cwTransSelected.some(tran => tran.tranNote),
      tooltip: !cwTran.availableActions?.includes('editNote')
        ? 'You do not have permissions to delete note'
        : !cwTransSelected.some(tran => tran.tranNote)
        ? 'No notes available for the selected transaction(s).'
        : '',
      action: () => taskService.deleteNotes(cwTransSelected),
    },
    {
      name: 'Edit Spec Lots',
      disabled: !(
        cwTran.availableActions?.includes('editSpecLotMatch') &&
        cwTransSelected.length === 1 &&
        cwTran.secId != undefined &&
        cwTran.legacyOrigId != undefined
      ),
      tooltip: !cwTran.availableActions?.includes('editSpecLotMatch')
        ? 'You do not have permissions to edit spec lots'
        : !(cwTransSelected.length === 1)
        ? 'You may only edit the spec lots for a single transaction'
        : '',
      action: () => taskService.editSpecLots(cwTran, params),
    },
    {
      name: 'Edit Transaction' + suffix,
      disabled:
        !isTransSameCategory ||
        !isTransEditable ||
        Boolean(isBulkTransferOutNonEditableTransaction),
      tooltip: !isTransSameCategory
        ? 'All transactions should be of same category'
        : !isTransEditable
        ? 'Some of the selected transactions are not Editable'
        : isBulkTransferOutNonEditableTransaction
        ? isBulkTransferOutNonEditableTransaction
        : '',
      action: () => {
        cwTransSelected.length == 1
          ? taskService.editClearwaterTransaction(cwTran)
          : taskService.bulkEditTrans(
              selectedTransId,
              selectedTransCode,
              securityId,
              securityData,
              Boolean(
                cwTransSelected[0].xferAccountId &&
                  cwTransSelected[0].xferAccountId != -1,
              ),
            )
      },
    },
    'separator',
    {
      name: 'Edit Type Label' + suffix,
      disabled: !isTransEditable,
      tooltip: !isTransEditable
        ? 'Some of the selected transactions are not Editable'
        : '',
      action: () => taskService.editTransactionTypeLabel(cwTransSelected),
    },
    {
      name: 'Remove Type Label' + suffix,
      disabled:
        cwTransSelected.some(cwTrans => !cwTrans.typeLabel) || !isTransEditable,
      tooltip: cwTransSelected.some(cwTrans => !cwTrans.typeLabel)
        ? 'No type label available for the selected transaction(s).'
        : !isTransEditable
        ? 'Some of the selected transactions are not Editable'
        : '',
      action: () => taskService.removeTransactionTypeLabel(cwTransSelected),
    },
    'separator',
    {
      name: 'Move to Different Sleeve' + suffix,
      action: () => taskService.moveToDifferentSleeve(cwTransSelected),
      disabled: !isSleeveAggregateLoaded || isCashTransaction(cwTransSelected),
      tooltip: !isSleeveAggregateLoaded
        ? 'This functionality is available only for Parent Portfolio Accounts'
        : isCashTransaction(cwTransSelected)
        ? 'Selected transactions are not of the CASH type'
        : undefined,
    },
    'separator',
    ...highlightService.createHighlightMenuItems(params),
  ]
}

/**
 * Check if all selected Transaction is of Cash Type
 */
export function isCashTransaction(cwTrans: ClearwaterTransaction[]): boolean {
  return cwTrans.some(
    tran =>
      !CASH_ENTRIES.includes(
        TransactionTypeEnum[getTransactionTypeKeyFromCode(tran.tranTypeCode)],
      ),
  )
}

/**
 * Get tooltip string for match action
 */
export function getMatchMenuItemTooltip(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
): string {
  const errorMsg = getMatchErrorMessage(cwTrans, extTrans)
  if (errorMsg !== undefined) {
    return errorMsg
  }

  if (cwTrans.length > 1) {
    return 'Match selected transactions to selected external transaction'
  }

  return 'Match selected transaction to selected external transactions'
}

/**
 * Check if the unmatch context menu option is disabled.
 */
export function checkIsUnmatchMenuItemDisabled(
  cwTrans: ClearwaterTransaction[],
): boolean {
  const someTransLinked =
    cwTrans.length !== 0 && cwTrans.some(tx => isLinked(tx))
  return (
    !someTransLinked && !checkSomeAvailableActionsIncludes(cwTrans, 'unmatch')
  )
}
/**
 * Check that all selected transaction is of same category  or not
 */
export function checkTransactionsCategory(
  selectedRows: ClearwaterTransaction[],
): boolean {
  let transType = getTransactionTypeKeyFromCode(selectedRows[0].tranTypeCode)
  if (selectedRows.length > 1) {
    const tranCategory = getTransactionCategoryFromTypeKeyForBulk(transType)
    for (const row of selectedRows) {
      transType = getTransactionTypeKeyFromCode(row.tranTypeCode)
      if (tranCategory != getTransactionCategoryFromTypeKeyForBulk(transType)) {
        return false
      }
    }
  } else {
    const tranCategory = getTransactionCategoryFromTypeKey(transType)
    for (const row of selectedRows) {
      transType = getTransactionTypeKeyFromCode(row.tranTypeCode)
      if (tranCategory != getTransactionCategoryFromTypeKey(transType)) {
        return false
      }
    }
  }
  return true
}
/**
 * Get tooltip string for matchUsingExternal action
 */
export function getMatchUsingExternalMenuItemTooltip(
  cwTrans: ClearwaterTransaction[],
  extTrans: ExternalTransaction[],
): string {
  const errorMsg = getMatchUsingExternalErrorMessage(cwTrans, extTrans)
  const isCwSameCategory = checkTransactionsCategory(cwTrans)
  if (!isCwSameCategory) {
    return 'All selected Clearwater Transaction should be of same category'
  }
  if (extTrans.length > 1) {
    return 'Select only one external transaction'
  }
  if (errorMsg !== undefined) {
    return errorMsg
  }
  return 'Match selected external transactions to selected clearwater transaction'
}

/**
 * checkBulkTransferOutNonEditableTransaction TRNO/EXOT bulk edit is not possibly unless all cw trans have loaded and in the selected ones all are single sided(empty xrefAccountId)
 */
export function checkBulkTransferOutNonEditableTransaction(
  cwTransactionsSelected: ClearwaterTransaction[],
): string | false {
  if (cwTransactionsSelected.length <= 1) {
    //its not a bulk selection so return false
    return false
  }
  if (cwTransactionsSelected.some(cwTrans => cwTrans.tranTypeCode != 'TRNO')) {
    // its not all TRNO
    return false
  }

  if (cwTransactionsSelected.some(cwTrans => !cwTrans.xferAccountId)) {
    //a) some of the xfer is not calculated so dont allow edit
    return 'Xfer accountId value is unavailable for a few selected TRNOs'
  }

  if (
    cwTransactionsSelected.every(cwTrans => cwTrans.xferAccountId != -1) ||
    cwTransactionsSelected.every(cwTrans => cwTrans.xferAccountId === -1)
  ) {
    /**b) every tran xfer is -1(single sided).Meaning all are 2 sided so allow edit
      c) every tran xfer is -1 .Meaning all are single sided so allow edit
    **/

    return false
  }

  // if we are here it means we have all xfers determined but they are mix of 2 sided and single sided TRNOs
  return 'All TRNOs selected should either be single sided or two sided'
}
